import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import { styles } from "./input-style";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { colors } from "@/utils/const";
import { useI18n } from "@/i18n/I18nProvider";
import { arFont, enFont } from "@/styles/font";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export default function Input({
  label,
  value,
  handleChange,
  placeholder,
  type,
  disabled = false,
  endIcon,
  customStyle = {},
  inputStyle = {},
  name,
  valuesOption = [],
  handleBlur,
  error,
  flag,
  shrink,
  getOptionValue,
  getOptionLabel,
  ...reset
}) {
  const [focused, setFocused] = useState(false);
  const { locale } = useI18n();
  const isAr = locale === "ar";

  const isNumeric =
    reset?.inputMode === "numeric" || type === "number" || type === "tel";

  const menuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          minWidth: "100%",
          direction: isAr ? "rtl" : "ltr",
          textAlign: isAr ? "right" : "left",
        },
      },
      MenuListProps: {
        sx: {
          direction: isAr ? "rtl" : "ltr",
          textAlign: isAr ? "right" : "left",
          paddingY: 0.5,
        },
      },
      anchorOrigin: {
        vertical: "bottom",
        horizontal: isAr ? "right" : "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: isAr ? "right" : "left",
      },
    }),
    [isAr],
  );

  return type === "selectbox" ? (
    <div>
      <FormControl
        disabled={disabled}
        sx={{
          ...styles.input,
          ...customStyle,
          position: "relative",
          "& .MuiInputLabel-outlined": {
            right: isAr ? "30px" : "auto",
            left: isAr ? "auto" : "0px",
            transformOrigin: isAr ? "top right" : "top left",
          },

          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            right: isAr ? "25px" : "auto",
            left: isAr ? "auto" : "0px",
          },

          "&.Mui-disabled.MuiInputLabel-shrink": {
            color: colors.black,
          },
          width: "100%",
        }}
      >
        <InputLabel
          shrink={focused || shrink}
          sx={{
            fontSize: "14px",
            color: "rgba(0, 8, 20, 0.70)",

            "&.MuiInputLabel-shrink": {
              fontSize: "16px",
              color: "#282781",
            },
            "&.Mui-focused": {
              color: "#282781",
            },
            "&&.Mui-disabled": {
              color: "rgba(0,0,0,0.4) !important",
            },
            "&&.Mui-disabled.MuiInputLabel-shrink": {
              color: colors.black,
            },
            fontWeight: 400,
            backgroundColor: "white",
            zIndex: 1,
            pointerEvents: "none",
            transformOrigin: isAr ? "top right" : "top left",
          }}
        >
          {label}
        </InputLabel>

        <Select
          disabled={disabled}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            handleBlur && handleBlur(e);
            setFocused(false);
          }}
          onChange={(e) => handleChange(e.target.value, name)}
          input={<OutlinedInput />}
          MenuProps={menuProps}
          autoWidth={false}
          sx={{
            "& .MuiSelect-icon": {
              right: isAr && "auto",
              left: isAr ? 8 : "auto",
            },
            "& .MuiSelect-select": {
              direction: isAr ? "rtl" : "ltr",
              textAlign: isAr ? "right" : "left",
              paddingRight: "12px !important",
              fontSize: "14px",
            },
          }}
          renderValue={(selected) => {
            const found = valuesOption.find(
              (o) => String(getOptionValue(o)) === String(selected),
            );
            return found ? getOptionLabel(found) : "";
          }}
        >
          {valuesOption.map((opt) => {
            const v = getOptionValue(opt);
            const lbl = getOptionLabel(opt);

            return (
              <MenuItem
                key={String(v)}
                value={v}
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  direction: isAr ? "rtl" : "ltr",
                  textAlign: isAr ? "right" : "left",
                  "&.Mui-selected": {
                    backgroundColor: `${colors.blue} !important`,
                    color: "white !important",
                  },
                }}
                className={isAr ? arFont.className : enFont.className}
              >
                {lbl}
              </MenuItem>
            );
          })}
        </Select>

        {error && (
          <Box sx={{ color: "#E34935", fontSize: "10px", m: 1 }}>{error}</Box>
        )}
      </FormControl>
    </div>
  ) : (
    <Box sx={{ width: "100%" }}>
      <TextField
        id={label}
        value={value}
        label={label}
        onChange={(e) => handleChange(e.target.value, name)}
        onBlur={(e) => {
          handleBlur && handleBlur(e);
          setFocused(false);
        }}
        onFocus={() => setFocused(true)}
        disabled={disabled}
        variant="outlined"
        sx={{
          ...styles.input,
          width: "100%",
          ...customStyle,
          "& .MuiInputLabel-outlined": {
            right: isAr ? "30px" : "auto",
            left: isAr ? "auto" : "50px",
          },
          "& .MuiInputLabel-root": {
            right: isAr ? "20px" : "auto",
            left: isAr ? "auto" : "-10px",
            fontSize: "14px",
          },

          "& .MuiInputLabel-root.MuiInputLabel-shrink": {
            right: isAr ? "20px" : "auto",
            left: isAr ? "auto" : "-40px",
          },
        }}
        placeholder={placeholder}
        type={type}
        InputLabelProps={{
          shrink: Boolean(value) || focused,
          sx: {
            fontWeight: 400,
            right: 0,
            transformOrigin: "top right",
            backgroundColor: "white",
            paddingX: "10px",
            zIndex: 1,
            pointerEvents: "none",
            transition: "all 0.2s ease-in-out",
          },
        }}
        InputProps={{
          style: {
            ...inputStyle,
          },
          endAdornment: endIcon && (
            <InputAdornment position="end">{endIcon}</InputAdornment>
          ),
        }}
        {...reset}
      />
      {error && (
        <Box sx={{ color: "#E34935", fontSize: "12px", m: 1 }}>{error}</Box>
      )}
    </Box>
  );
}
